import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from '@magnetic/skeleton';
import WorkSpacesLogoSVG from '../../../../assets/images/workspaces-logo.svg';
import { useTranslation } from 'react-i18next';
import Loader from '../../../../common/loader/loader';
import Error from '../../../../common/error/error';
import PoweredByBrand from '../../../../common/powered-by-brand';
import { ScalableImage } from '../../../../common/styles/commonstyles';
import { configureHeartbeatUpdateMessage, configureSendWebSocketMessage, updateDeviceToken, updateStatus, } from '../../../../store/configureslice';
import { DataService } from '../../service/deviceservice';
import { subscribeToWebSocketEvent } from './../../../../store/configuremiddleware';
import { DisplayCodeActivation, DisplayCodeDisplay, DisplayCodeSteps, Footer, ResponsiveContent, VerticalLine, } from './display-code-elements';
import { useNavigate } from 'react-router-dom';
const getDomain = (dataService) => {
    let domain = dataService.getDomain();
    if (!domain) {
        console.log('Domain not found in cache, fetching from location.hostname');
        domain = location.hostname;
        dataService.setDomain(domain);
    }
    return domain;
};
const DisplayCode = ({ tm, devciceId, orgId, isNav, }) => {
    const dataService = new DataService();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [codeToShow, setCodeToShow] = useState(null);
    const domain = getDomain(dataService);
    // change this url
    const serverUrl = `wss://${domain}/init`;
    console.log('Signage server URL:', serverUrl);
    const isConnected = useSelector((state) => state.configure.isConnected);
    const isWSInitialized = useRef(false); // Track if WebSocket was initialized
    const signageActivated = useSelector((state) => state.configure.isActivated);
    let deviceToken = useSelector((state) => state.configure.token);
    // Check if device token exists in frame URL (legacy Signage case)
    if (!deviceToken) {
        const workspacesUrl = dataService.getFrameUrl();
        console.log('Frame URL from old Signage :: ', workspacesUrl);
        const token = new URLSearchParams(workspacesUrl).get('token');
        if (token) {
            dataService.setDeviceToken(token);
            dispatch(updateDeviceToken(token || ''));
        }
    }
    const navigate = useNavigate();
    const webGL = (() => {
        try {
            const canvas = document.createElement('canvas');
            return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
        }
        catch (e) {
            return false;
        }
    })();
    const connectWebSocket = useCallback(() => {
        if (isWSInitialized.current || tm === '' || tm === null)
            return; // Avoid reinitializing
        isWSInitialized.current = true;
        dataService.setTenantIdMasked(tm);
        dataService.setDeviceId(devciceId);
        dataService.setOrgId(orgId);
        dataService.setIsNavigator(isNav);
        dispatch({
            type: 'CONFIGURE_APP_START',
            payload: { serverUrl: serverUrl, webGL: webGL },
        });
        let unsubscribeShowFrameUpdate;
        let unsubscribeSetTokenUpdate;
        let unsubscribeInitUpdate;
        if (!deviceToken) {
            unsubscribeInitUpdate = subscribeToWebSocketEvent('init', (payload) => {
                console.log('Init message received:', payload);
                if (payload.code)
                    dataService.setActivationCode(payload.code);
                dataService.setTenantIdMasked(tm);
                // set uid only if it is not already set, which means this signage is not configured yet
                if (!dataService.getUid() && payload.uid) {
                    dataService.setUid(payload.uid);
                }
                if (!dataService.getDeviceType())
                    dataService.setDeviceType(payload.deviceType);
                setCodeToShow(dataService.getActivationCode());
                // TODO codeToShow won't be avaulable immediately, so use payload.code
                console.log('Activation code:', codeToShow + ' activation code received ' + payload.code);
            });
            unsubscribeSetTokenUpdate = subscribeToWebSocketEvent('setToken', (payload) => {
                console.log('SetToken message received:', payload);
                if (payload.status === true ||
                    (dataService.getUid() && dataService.getOrgId())) {
                    dataService.setRMSToken(payload.token);
                    dataService.setActivated(true);
                    dataService.setDeviceName(payload.deviceName);
                    dispatch(updateStatus(true));
                    const heartbeatContext = {
                        event: 'heartbeat',
                        payload: { token: dataService.getRMSToken() },
                    };
                    dispatch(configureHeartbeatUpdateMessage(heartbeatContext));
                    const fetchUrlEvent = {
                        event: 'fetchUrl',
                        payload: { token: payload.token },
                        timestamp: Date.now(),
                    };
                    dispatch(configureSendWebSocketMessage(fetchUrlEvent));
                    console.log('set Token update received and status recevied ' + payload.status);
                }
                else {
                    console.error('set Token updete failed and payload recevied ' +
                        JSON.stringify(payload));
                }
            });
            unsubscribeShowFrameUpdate = subscribeToWebSocketEvent('showFrame', (payload) => {
                console.log('ShowFrame message received:', payload);
                if (payload) {
                    const deviceToken = payload;
                    if (deviceToken) {
                        dataService.setDeviceToken(deviceToken);
                        dispatch(updateDeviceToken(deviceToken));
                    }
                    else {
                        console.error('ShowFrame Event Failed due token extraction failure ' +
                            JSON.stringify(payload));
                    }
                }
                else {
                    console.error('ShowFrame Event Failed  since url part of the payload is null ' +
                        JSON.stringify(payload));
                }
            });
        }
        else {
            console.log('Device token already exists in the cache, so redirecting to workspaces');
        }
        return () => {
            unsubscribeShowFrameUpdate();
            unsubscribeSetTokenUpdate();
            unsubscribeInitUpdate();
        };
    }, [isWSInitialized, codeToShow]);
    useEffect(() => {
        connectWebSocket(); // Initialize WebSocket only once
    }, [dispatch]);
    useEffect(() => {
        console.log('Redux isConnected state updated:', isConnected);
    }, [isConnected]);
    if (tm === '')
        return _jsx(Error, {});
    if (!isConnected) {
        return _jsx(Loader, {});
    }
    function showActivationCode() {
        return (_jsxs(_Fragment, { children: [_jsxs(DisplayCodeSteps, { children: [_jsx("p", Object.assign({ className: "display-code-follow" }, { children: t('configure.display-code.follow-steps-text') })), _jsxs("ol", { children: [_jsx("li", { children: t('configure.display-code.follow-steps-login') }), _jsx("li", { children: t('configure.display-code.follow-steps-launch-exp-app') }), _jsxs("li", { children: [t('configure.display-code.follow-steps-add-non-webex'), _jsx("span", { children: t('configure.display-code.follow-steps-goto-devices') })] }), _jsx("li", { children: t('configure.display-code.follow-steps-enter-activation-code') })] })] }), _jsxs(DisplayCodeDisplay, Object.assign({ "$signage_activated": signageActivated.toString() }, { children: [_jsxs("div", Object.assign({ className: "display-code-header" }, { children: [' ', t('configure.display-code.activation-code-title')] })), codeToShow ? (_jsxs("div", Object.assign({ className: "display-code-code" }, { children: [" ", codeToShow] }))) : (_jsx(Skeleton, { shade: "regular", height: 180, style: {
                                width: 400,
                                marginTop: 40,
                                marginBottom: 40,
                            } })), _jsx("div", Object.assign({ className: "display-code-workspaces-logo" }, { children: _jsx(ScalableImage, { width: "100%", src: WorkSpacesLogoSVG }) })), _jsxs("div", Object.assign({ className: "display-code-logo-text" }, { children: [' ', t('common.smartworkspaces.moto')] }))] }))] }));
    }
    function showActivationSuccess() {
        var _a;
        const isWebex = dataService.getDeviceType() === 'webex';
        const findYourDeviceText = isWebex
            ? t('configure.display-code.post-activate-steps-provision-content-webex')
            : t('configure.display-code.post-activate-steps-provision-content');
        const clickConfigureText = t('configure.display-code.post-activate-steps-provision-content-click');
        const deviceName = (_a = dataService.getDeviceName()) !== null && _a !== void 0 ? _a : '';
        const configureContentText = `${findYourDeviceText} ${deviceName} ${clickConfigureText}`;
        return (_jsxs(_Fragment, { children: [_jsxs(DisplayCodeDisplay, Object.assign({ "$signage_activated": signageActivated.toString() }, { children: [_jsx("div", Object.assign({ className: "display-code-workspaces-logo" }, { children: _jsx(ScalableImage, { width: "100%", src: WorkSpacesLogoSVG }) })), _jsxs("div", Object.assign({ className: "display-code-logo-text" }, { children: [' ', t('common.smartworkspaces.moto')] }))] })), _jsx(VerticalLine, {}), _jsxs(DisplayCodeSteps, { children: [_jsx("p", Object.assign({ className: "display-code-follow" }, { children: t('configure.display-code.post-activate-steps-text') })), _jsxs("ol", { children: [_jsx("li", { children: t('configure.display-code.post-activate-steps-login') }), _jsx("li", { children: t('configure.display-code.post-activate-steps-launch-exp-app') }), _jsx("li", { children: t('configure.display-code.post-activate-steps-goto-devices') }), _jsx("li", { children: configureContentText })] })] })] }));
    }
    if (deviceToken) {
        navigate('/home', { state: { token: deviceToken } });
        return null;
    }
    else
        return (_jsx(_Fragment, { children: _jsxs(ResponsiveContent, { children: [_jsx(DisplayCodeActivation, { children: signageActivated === true
                            ? showActivationSuccess()
                            : showActivationCode() }), _jsx(Footer, { children: _jsx(PoweredByBrand, {}) })] }) }));
};
export default DisplayCode;
